import { useState } from 'react';
import { useTwilioDevice } from '@/hooks/useTwilioDevice';
import { useCallerIdOptions } from '@/hooks/useCallerIdOptions';
import { useCallLog } from '@/hooks/useCallLog';
import { DialPad, type CallMeta } from './DialPad';
import { RecentCallsList } from './RecentCallsList';
import { CallControls } from './CallControls';
import { IncomingCallBanner } from './IncomingCallBanner';
import { NetworkQualityIndicator } from './NetworkQualityIndicator';
import { Button } from '@/components/ui/button';
import { Phone, PhoneOff, Minimize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

type IdleTab = 'keypad' | 'recent';

export function Softphone() {
  const {
    status,
    activeCall,
    incomingCall,
    isMuted,
    callDuration,
    networkWarnings,
    makeCall,
    hangUp,
    toggleMute,
    sendDigits,
    acceptIncoming,
    rejectIncoming,
  } = useTwilioDevice();

  const { options: callerIdOptions, selectedId: selectedCallerId, setSelectedId: setSelectedCallerId } = useCallerIdOptions();
  const { data: recentCalls = [] } = useCallLog(10);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [idleTab, setIdleTab] = useState<IdleTab>('keypad');

  const handleCall = (number: string, meta?: CallMeta) => {
    makeCall(number, meta);
  };

  const handleRedial = (number: string, meta?: { contactId?: string; leadId?: string; accountId?: string }) => {
    const callMeta: CallMeta = { ...meta };
    if (selectedCallerId) callMeta.callerIdNumberId = selectedCallerId;
    makeCall(number, callMeta);
  };

  // Always show for active/incoming calls, otherwise respect visibility toggle
  if (!isVisible && !activeCall && !incomingCall) {
    return (
      <Button
        size="icon"
        variant="outline"
        className="fixed bottom-[calc(5rem_+_env(safe-area-inset-bottom,0px))] md:bottom-4 right-4 z-30 h-10 w-10 rounded-full shadow-lg"
        onClick={() => setIsVisible(true)}
      >
        <Phone className="h-4 w-4" />
      </Button>
    );
  }

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Incoming call banner (always show regardless of expand state)
  if (incomingCall) {
    return (
      <IncomingCallBanner
        from={incomingCall.from}
        onAccept={acceptIncoming}
        onReject={rejectIncoming}
      />
    );
  }

  // Active call pill (minimized)
  if (activeCall && !isExpanded) {
    return (
      <div className="fixed bottom-[calc(5rem_+_env(safe-area-inset-bottom,0px))] md:bottom-4 right-4 z-30 flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full shadow-lg cursor-pointer"
        onClick={() => setIsExpanded(true)}>
        <Phone className="h-4 w-4 animate-pulse" />
        <span className="text-sm font-medium">{activeCall.remoteNumber}</span>
        <span className="text-sm tabular-nums">{formatDuration(callDuration)}</span>
        <NetworkQualityIndicator warnings={networkWarnings} />
        <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-red-600 rounded-full"
          onClick={(e) => { e.stopPropagation(); hangUp(); }}>
          <PhoneOff className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Expanded softphone panel
  return (
    <div className={cn(
      "fixed bottom-[calc(5rem_+_env(safe-area-inset-bottom,0px))] md:bottom-4 right-4 z-30 w-72 bg-background border rounded-lg shadow-xl",
      activeCall && "ring-2 ring-green-500"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b">
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4" />
          <span className="text-sm font-medium">
            {status === 'ready' ? 'Phone' : status === 'busy' ? 'On Call' : status === 'initializing' ? 'Connecting...' : 'Offline'}
          </span>
          {status === 'ready' && <span className="h-2 w-2 rounded-full bg-green-500" />}
          {status === 'error' && <span className="h-2 w-2 rounded-full bg-red-500" />}
        </div>
        <div className="flex items-center gap-1">
          {activeCall && (
            <Button size="icon" variant="ghost" className="h-7 w-7"
              onClick={() => setIsExpanded(false)}>
              <Minimize2 className="h-3.5 w-3.5" />
            </Button>
          )}
          {!activeCall && (
            <Button size="icon" variant="ghost" className="h-7 w-7"
              onClick={() => setIsVisible(false)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Active call info */}
      {activeCall && (
        <div className="p-3 text-center border-b">
          <p className="text-lg font-mono">{activeCall.remoteNumber}</p>
          <p className="text-sm text-muted-foreground">
            {activeCall.direction === 'inbound' ? 'Incoming' : 'Outgoing'} &middot; {formatDuration(callDuration)}
          </p>
          <NetworkQualityIndicator warnings={networkWarnings} />
        </div>
      )}

      {/* Call controls (during call) */}
      {activeCall && (
        <CallControls
          isMuted={isMuted}
          onToggleMute={toggleMute}
          onHangUp={hangUp}
          onSendDigits={sendDigits}
        />
      )}

      {/* Idle tabs: Keypad / Recent */}
      {!activeCall && status === 'ready' && (
        <>
          <div className="flex border-b">
            <button
              className={cn(
                "flex-1 py-2 text-xs font-medium text-center transition-colors",
                idleTab === 'keypad' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => setIdleTab('keypad')}
            >
              Keypad
            </button>
            <button
              className={cn(
                "flex-1 py-2 text-xs font-medium text-center transition-colors",
                idleTab === 'recent' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => setIdleTab('recent')}
            >
              Recent
            </button>
          </div>

          {idleTab === 'keypad' ? (
            <DialPad
              onCall={handleCall}
              callerIdOptions={callerIdOptions}
              selectedCallerId={selectedCallerId}
              onCallerIdChange={setSelectedCallerId}
            />
          ) : (
            <RecentCallsList calls={recentCalls} onRedial={handleRedial} />
          )}
        </>
      )}

      {/* Status messages */}
      {(status === 'initializing' || status === 'offline') && !activeCall && (
        <div className="p-4 text-center text-sm text-muted-foreground">
          {status === 'initializing' ? 'Connecting to phone service...' : 'Phone is offline'}
        </div>
      )}
      {status === 'error' && !activeCall && (
        <div className="p-4 text-center text-sm text-destructive">
          Phone connection error. Check your microphone permissions.
        </div>
      )}
    </div>
  );
}
